import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import SectionBadge from '../components/ui/SectionBadge';
import CtaBanner from '../components/home/CtaBanner';
import { caseStudies } from '../lib/caseStudiesData';

const ProjectCard = ({ project, index, large = false }) => (
  <Link
    to={`/case-studies/${project.slug}`}
    className={`project-card group block overflow-hidden rounded-[1.75rem] border border-[#D5E0E5] bg-white transition duration-300 hover:-translate-y-1 hover:border-[#AFC4CE] hover:shadow-[0_24px_70px_rgba(16,42,58,0.12)] ${large ? 'lg:grid lg:grid-cols-[1.25fr_.75fr]' : ''}`}
    aria-label={`Read the ${project.name} case study`}
  >
    <div className={`overflow-hidden bg-[#EAF0F3] ${large ? 'aspect-[16/10] lg:aspect-auto lg:min-h-[520px]' : 'aspect-[16/10]'}`}>
      <img
        src={project.image}
        alt={`${project.name} website preview`}
        width="1440"
        height="900"
        loading={index < 2 ? 'eager' : 'lazy'}
        className="h-full w-full object-cover object-top transition duration-700 ease-out group-hover:scale-[1.025]"
      />
    </div>
    <div className={`flex flex-col p-7 md:p-9 ${large ? 'justify-between lg:p-10' : ''}`}>
      <div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: project.accent }}>{project.category}</p>
          <span className="text-xs font-medium text-[#8A9AA3]">{project.year}</span>
        </div>
        <h2 className={`${large ? 'mt-8 text-3xl md:text-4xl' : 'mt-5 text-2xl'} font-semibold tracking-[-0.035em] text-[#102A3A]`}>{project.name}</h2>
        <p className={`${large ? 'mt-5 text-lg leading-8' : 'mt-4 text-sm leading-6'} text-[#63747E]`}>{project.summary}</p>
      </div>
      <div className={`${large ? 'mt-12' : 'mt-7'} flex items-center justify-between border-t border-[#DCE4E8] pt-5`}>
        <span className="text-sm font-semibold text-[#176B8A]">Read case study</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E7F0F4] text-[#176B8A] transition duration-300 group-hover:translate-x-1 group-hover:bg-[#176B8A] group-hover:text-white" aria-hidden="true">&rarr;</span>
      </div>
    </div>
  </Link>
);

const CaseStudies = () => (
  <>
    <Helmet>
      <title>Selected Work & Case Studies | Nexgeius Toronto</title>
      <meta name="description" content="Explore websites and software built by Nexgeius for legal services, field operations, SaaS, sports data, and commerce." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://www.nexgeius.com/case-studies" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Selected Work & Case Studies | Nexgeius" />
      <meta property="og:description" content="Real websites and software, presented without invented numbers." />
      <meta property="og:url" content="https://www.nexgeius.com/case-studies" />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Nexgeius selected work',
        itemListElement: caseStudies.map((project, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: project.name,
          url: `https://www.nexgeius.com/case-studies/${project.slug}`,
        })),
      })}</script>
    </Helmet>

    <main>
      <section className="page-grid bg-[#F6F8FA] px-6 pb-16 pt-32 lg:px-8 lg:pb-20">
        <div className="relative z-10 mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div>
            <SectionBadge>Selected work</SectionBadge>
            <h1 className="mt-6 max-w-4xl text-[clamp(2.8rem,6vw,5.8rem)] font-semibold leading-[0.96] tracking-[-0.055em] text-[#102A3A]">The work should make the case.</h1>
          </div>
          <div className="max-w-xl lg:justify-self-end">
            <p className="text-lg leading-8 text-[#52636E]">Live work across service businesses, operations, software, data, and commerce. Open any project to see the thinking behind it, then visit the product itself.</p>
            <p className="mt-5 text-sm font-semibold text-[#176B8A]">Every project card is clickable.</p>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F8FA] px-6 pb-24 lg:px-8 lg:pb-28">
        <div className="mx-auto max-w-7xl">
          <ProjectCard project={caseStudies[0]} index={0} large />
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {caseStudies.slice(1).map((project, index) => (
              <ProjectCard key={project.slug} project={project} index={index + 1} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#DCE4E8] bg-white px-6 py-16 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="section-kicker">Your project could be next</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Have a business problem worth solving?</h2>
          </div>
          <Link to="/contact" className="button-primary inline-flex items-center gap-2 md:justify-self-end">Start a project <span aria-hidden="true">&rarr;</span></Link>
        </div>
      </section>

      <CtaBanner />
    </main>
  </>
);

export default CaseStudies;
